import { Fragment, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { TypeId } from './api';
import { FAMILY_CLASS, familyOfKind, typeOfKind } from './placeholders';

export interface Highlight {
  /** The summary chip chosen: its placeholders are put in relief. */
  readonly type: TypeId | null;
  /** The occurrence the arrows stand on, by position in reading order. */
  readonly current: number | null;
}

interface Counter {
  next: number;
}

function Placeholder({
  label,
  index,
  highlight,
}: {
  readonly label: string;
  readonly index: number;
  readonly highlight: Highlight;
}) {
  const kind = label.slice(1, label.lastIndexOf('_'));
  const type = typeOfKind(kind);
  const isCurrent = highlight.current === index;
  const inChosenType = highlight.type !== null && type === highlight.type;
  return (
    <mark
      data-placeholder={index}
      data-family={familyOfKind(kind)}
      data-current={isCurrent ? 'true' : undefined}
      className={cn(
        'rounded px-1 font-mono text-[0.92em] font-semibold',
        FAMILY_CLASS[familyOfKind(kind)],
        inChosenType && 'outline-2 outline-offset-1 outline-[var(--focus)]',
        isCurrent && 'bg-[var(--focus)] text-white outline-[var(--focus)]',
      )}
    >
      {label}
    </mark>
  );
}

const INLINE = /(\*\*[^*]+\*\*|\[[A-Z][A-Z_]*_\d+\])/g;
const PLACEHOLDER_ONLY = /^\[[A-Z][A-Z_]*_\d+\]$/;

function inline(text: string, counter: Counter, highlight: Highlight): ReactNode[] {
  return text.split(INLINE).map((part, i) => {
    if (PLACEHOLDER_ONLY.test(part)) {
      return <Placeholder key={i} label={part} index={counter.next++} highlight={highlight} />;
    }
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={i}>{inline(part.slice(2, -2), counter, highlight)}</strong>;
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}

const isTableRow = (line: string) => /^\s*\|.*\|\s*$/.test(line);
const isTableRule = (line: string) => /^\s*\|[\s:|-]+\|\s*$/.test(line);
const cells = (line: string) =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());

interface Block {
  readonly node: ReactNode;
  /** The index of the first line after the block. */
  readonly next: number;
}

const LIST_ITEM = /^\s*([-*]|\d+\.)\s+/;

function headingBlock(
  heading: RegExpExecArray,
  i: number,
  counter: Counter,
  highlight: Highlight,
): Block {
  const content = inline(heading[2]!, counter, highlight);
  const node =
    heading[1]!.length === 1 ? (
      <h3 key={i} className="mt-0 mb-3 text-xl font-bold">
        {content}
      </h3>
    ) : (
      <h4 key={i} className="mt-5 mb-2 text-base font-bold">
        {content}
      </h4>
    );
  return { node, next: i + 1 };
}

function tableBlock(lines: string[], start: number, counter: Counter, highlight: Highlight): Block {
  const rows: string[][] = [];
  let header: string[] | null = null;
  let i = start;
  while (i < lines.length && isTableRow(lines[i]!)) {
    const current = lines[i]!;
    if (isTableRule(current)) header = rows.pop() ?? null;
    else rows.push(cells(current));
    i += 1;
  }
  const node = (
    <div key={`t${i}`} className="my-3 overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        {header && (
          <thead>
            <tr>
              {header.map((c, k) => (
                <th key={k} className="border-b border-border px-2 py-1.5 text-left">
                  {inline(c, counter, highlight)}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((r, k) => (
            <tr key={k}>
              {r.map((c, m) => (
                <td key={m} className="border-b border-border px-2 py-1.5">
                  {inline(c, counter, highlight)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  return { node, next: i };
}

function listBlock(lines: string[], start: number, counter: Counter, highlight: Highlight): Block {
  const ordered = /^\s*\d+\./.test(lines[start]!);
  const items: ReactNode[] = [];
  let i = start;
  while (i < lines.length && LIST_ITEM.test(lines[i]!)) {
    items.push(<li key={i}>{inline(lines[i]!.replace(LIST_ITEM, ''), counter, highlight)}</li>);
    i += 1;
  }
  const List = ordered ? 'ol' : 'ul';
  const node = (
    <List key={`l${i}`} className={cn('my-2 pl-5', ordered ? 'list-decimal' : 'list-disc')}>
      {items}
    </List>
  );
  return { node, next: i };
}

function paragraphBlock(
  lines: string[],
  start: number,
  counter: Counter,
  highlight: Highlight,
): Block {
  const paragraph: string[] = [];
  let i = start;
  while (
    i < lines.length &&
    lines[i]!.trim() !== '' &&
    !/^#{1,4}\s/.test(lines[i]!) &&
    !isTableRow(lines[i]!) &&
    !LIST_ITEM.test(lines[i]!)
  ) {
    paragraph.push(lines[i]!);
    i += 1;
  }
  const node = (
    <p key={`p${i}`} className="my-2">
      {inline(paragraph.join(' '), counter, highlight)}
    </p>
  );
  return { node, next: i };
}

function blockAt(lines: string[], i: number, counter: Counter, highlight: Highlight): Block {
  const line = lines[i]!;
  const heading = /^(#{1,4})\s+(.*)$/.exec(line);
  if (heading) return headingBlock(heading, i, counter, highlight);
  if (isTableRow(line)) return tableBlock(lines, i, counter, highlight);
  if (LIST_ITEM.test(line)) return listBlock(lines, i, counter, highlight);
  return paragraphBlock(lines, i, counter, highlight);
}

/**
 * The Markdown as a reading view: headings, paragraphs, lists, tables and bold, which is what the
 * pipeline writes. Built as elements (never as HTML), so nothing in the text can run.
 */
function RenderedMarkdown({ markdown, highlight }: { markdown: string; highlight: Highlight }) {
  const counter: Counter = { next: 0 };
  const lines = markdown.split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  while (i < lines.length) {
    if (lines[i]!.trim() === '') {
      i += 1;
      continue;
    }
    const block = blockAt(lines, i, counter, highlight);
    blocks.push(block.node);
    i = block.next;
  }
  return <div className="text-[15px] leading-relaxed">{blocks}</div>;
}

/** The raw text, as copied and downloaded, with the placeholders highlighted. */
function RawMarkdown({ markdown, highlight }: { markdown: string; highlight: Highlight }) {
  const counter: Counter = { next: 0 };
  return (
    <pre className="font-mono text-[13px] leading-relaxed break-words whitespace-pre-wrap">
      {markdown
        .split(/(\[[A-Z][A-Z_]*_\d+\])/g)
        .map((part, i) =>
          PLACEHOLDER_ONLY.test(part) ? (
            <Placeholder key={i} label={part} index={counter.next++} highlight={highlight} />
          ) : (
            <Fragment key={i}>{part}</Fragment>
          ),
        )}
    </pre>
  );
}

export function MarkdownView({
  markdown,
  mode,
  highlight,
}: {
  readonly markdown: string;
  readonly mode: 'rendered' | 'raw';
  readonly highlight: Highlight;
}) {
  return mode === 'rendered' ? (
    <RenderedMarkdown markdown={markdown} highlight={highlight} />
  ) : (
    <RawMarkdown markdown={markdown} highlight={highlight} />
  );
}
